import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '~/components/common/Button'
import { Typography } from '~/components/common/Typography'
import { Modal } from '~/components/modals/CurieModal'
import {
  MonitoringAlertFilterMode,
  MonitoringAlertsFilterDropdown
} from '~/components/monitoring/MonitoringAlertsFilterDropdown'
import { MonitoringConnectionsContent } from '~/components/monitoring/MonitoringConnectionsContent'
import { MonitoringMonitoredScopeContent } from '~/components/monitoring/MonitoringMonitoredScopeContent'
import { RepoSelectModal, UploadedRepo } from '~/components/repo-selection/LegacyRepoSelectModal'
import { DialogHeader } from '~/components/usage-dashboard/DialogHeader'
import { CommandType } from '~/constants/commands'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useIndexes } from '~/hooks'
import { bridge } from '~/services/bridge'
import {
  MonitoringAlert,
  getMonitoringAlerts,
  investigateMonitoringAlert
} from '~/services/monitoringService'
import type { RepositoryBranch } from '~/types/Conversations'
import { BetterToast } from '~/utils'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { storePendingInitialMessage } from '~/utils/initialMessageTransport'
import { extractFileDisplayName } from '~/utils/repositoryUtils'
import { datetimeLocalToUtc, parseBackendDate } from '~/utils/timezone'

interface Integration {
  conn_type: string
  connected: boolean
  metadata: { allowed_indices?: string[] } | null
}

interface MonitoringDashboardDialogProps {
  isOpen: boolean
  onClose: () => void
}

const DEFAULT_ALERT_FETCH_LIMIT = 25

function toAbsoluteDateTimeLabel(timestamp: string): string {
  const createdAt = parseBackendDate(timestamp)
  if (!createdAt || Number.isNaN(createdAt.getTime())) {
    return 'Unknown time'
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(createdAt)
}

function providerLabel(provider: string): string {
  if (!provider) {
    return 'Unknown'
  }
  return provider.charAt(0).toUpperCase() + provider.slice(1)
}

function alertTitle(alert: MonitoringAlert): string {
  const trimmedSummary = alert.summary?.trim()
  if (trimmedSummary) {
    return trimmedSummary
  }
  return `Alert #${alert.id}`
}

function mergeMonitoringAlerts(
  existingAlerts: MonitoringAlert[],
  incomingAlerts: MonitoringAlert[]
): MonitoringAlert[] {
  const existingIds = new Set(existingAlerts.map(alert => alert.id))
  const merged = [...existingAlerts]

  for (const alert of incomingAlerts) {
    if (!existingIds.has(alert.id)) {
      existingIds.add(alert.id)
      merged.push(alert)
    }
  }

  return merged
}

function MonitoringBellIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox='0 0 16 16' fill='none' className={className}>
      <circle cx='8' cy='8' r='6.5' stroke='currentColor' />
      <path d='M8 5.2V8.4' stroke='currentColor' strokeLinecap='round' />
      <circle cx='8' cy='10.8' r='0.7' fill='currentColor' />
    </svg>
  )
}

function ScopeIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox='0 0 16 16' fill='none' className={className}>
      <path
        d='M3 12.5L8 9.8M8 9.8L13 12.5M8 9.8V3.5'
        stroke='currentColor'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <circle cx='8' cy='3.5' r='1.5' stroke='currentColor' />
      <circle cx='3' cy='12.5' r='1.5' stroke='currentColor' />
      <circle cx='13' cy='12.5' r='1.5' stroke='currentColor' />
    </svg>
  )
}

function ConnectionsIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox='0 0 16 16' fill='none' className={className}>
      <circle cx='3' cy='3' r='1.4' stroke='currentColor' />
      <circle cx='13' cy='8' r='1.4' stroke='currentColor' />
      <circle cx='3' cy='13' r='1.4' stroke='currentColor' />
      <path d='M4.2 3.8L11.6 7.2M4.2 12.2L11.6 8.8' stroke='currentColor' />
    </svg>
  )
}

function SharedMembersIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox='0 0 16 16' fill='none' className={className}>
      <circle cx='8' cy='5' r='2.6' stroke='currentColor' />
      <path d='M3.5 13C4 10.9 5.8 9.4 8 9.4C10.2 9.4 12 10.9 12.5 13' stroke='currentColor' />
    </svg>
  )
}

function AlertIcon({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className={`${compact ? 'size-5' : 'size-6'} shrink-0 rounded-full border border-[rgba(0,0,0,0.22)] flex items-center justify-center text-[rgba(0,0,0,0.45)]`}
    >
      <span className={compact ? 'text-[12px] leading-[12px]' : 'text-[14px] leading-[14px]'}>
        !
      </span>
    </div>
  )
}

interface AlertItemProps {
  alert: MonitoringAlert & { displayProvider: string; displayTitle: string; displayTime: string }
  isCompact: boolean
  isInvestigating: boolean
  disabled: boolean
  onInvestigate: (alertId: number) => void
}

function AlertItem({ alert, isCompact, isInvestigating, disabled, onInvestigate }: AlertItemProps) {
  const actionLabel = isInvestigating
    ? 'Starting...'
    : isCompact
      ? 'Investigate'
      : 'Investigate Context'

  if (isCompact) {
    return (
      <div className='flex items-center gap-2 border-b border-[rgba(0,0,0,0.06)] px-2 py-[6px] last:border-b-0'>
        <AlertIcon compact />
        <div className='min-w-0 flex-1'>
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            fontWeight='medium'
            className='truncate text-[rgba(0,0,0,0.88)]'
          >
            {alert.displayTitle}
          </Typography>
          <Typography fontFamily='geist' fontSize='bodySm' className='text-[rgba(0,0,0,0.38)]'>
            {alert.displayProvider} · {alert.displayTime}
          </Typography>
        </div>
        <button
          type='button'
          disabled={disabled}
          onClick={() => void onInvestigate(alert.id)}
          className='shrink-0 rounded-md px-2 py-1 text-[#5b5bd5] hover:bg-[rgba(91,91,213,0.08)] disabled:cursor-not-allowed disabled:opacity-50'
        >
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            fontWeight='bold'
            className='text-[#5b5bd5]'
          >
            {actionLabel}
          </Typography>
        </button>
      </div>
    )
  }

  return (
    <div className='rounded-[24px] border border-[rgba(0,0,0,0.14)] bg-white px-6 py-6'>
      <div className='flex items-start gap-4'>
        <AlertIcon />
        <div>
          <Typography
            fontFamily='geist'
            fontSize='titleSm'
            fontWeight='bold'
            className='text-[rgba(0,0,0,0.88)]'
          >
            {alert.displayTitle}
          </Typography>
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            className='mt-[7px] text-[rgba(0,0,0,0.45)]'
          >
            {alert.displayProvider} · {alert.displayTime}
          </Typography>
          <button
            type='button'
            disabled={disabled}
            onClick={() => void onInvestigate(alert.id)}
            className='mt-[14px] inline-flex items-center gap-2 text-[#5b5bd5] disabled:cursor-not-allowed disabled:opacity-50'
          >
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='bold'
              className='text-[#5b5bd5]'
            >
              {actionLabel}
            </Typography>
            <svg viewBox='0 0 16 16' fill='none' className='size-4 text-[#5b5bd5]'>
              <path
                d='M6 4L10 8L6 12'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

const COMPACT_LAYOUT_MAX_WIDTH = 960

function useCompactView() {
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth <= COMPACT_LAYOUT_MAX_WIDTH
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mediaQuery = window.matchMedia(`(max-width: ${COMPACT_LAYOUT_MAX_WIDTH}px)`)

    const handleChange = (event: MediaQueryListEvent) => {
      setIsNarrowViewport(event.matches)
    }

    setIsNarrowViewport(mediaQuery.matches)
    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  return bridge.isInPlugin() || isNarrowViewport
}

type MonitoringTab = 'recent-alerts' | 'monitored-scope' | 'connections' | 'shared-members'

const sidebarItems: {
  key: MonitoringTab
  label: string
  icon: React.FC<{ className?: string }>
  disabled: boolean
}[] = [
  {
    key: 'recent-alerts',
    label: 'Recent alerts',
    icon: MonitoringBellIcon,
    disabled: false
  },
  {
    key: 'monitored-scope',
    label: 'Monitored Scope',
    icon: ScopeIcon,
    disabled: false
  },
  {
    key: 'connections',
    label: 'Connections',
    icon: ConnectionsIcon,
    disabled: false
  },
  {
    key: 'shared-members',
    label: 'Shared Members',
    icon: SharedMembersIcon,
    disabled: true
  }
]

interface MonitoringInvestigateRepoModalProps {
  isOpen: boolean
  workspaceUuid: string
  initialRepos: RepositoryBranch[]
  initialUploads: UploadedRepo[]
  onClose: () => void
  onConfirm: (data: {
    repositoryBranches: RepositoryBranch[]
    uploadedRepos: UploadedRepo[]
  }) => void
}

function MonitoringInvestigateRepoModal({
  isOpen,
  workspaceUuid,
  initialRepos,
  initialUploads,
  onClose,
  onConfirm
}: MonitoringInvestigateRepoModalProps) {
  const indexes = useIndexes({
    exculdeInternalRepos: true,
    commandType: CommandType.MEGA_QA_AGENT_V1
  })

  const repositories = useMemo(
    () =>
      indexes.map(index => ({
        conn_type: index.conn.conn_type,
        index_name: index.name.name,
        label: extractFileDisplayName(`${index.conn.conn_type}:${index.name.name}`)
      })),
    [indexes]
  )

  const getBranches = useCallback(
    (connType: string, indexName: string) =>
      indexes
        .filter(index => index.conn.conn_type === connType && index.name.name === indexName)
        .map(index => index.name.branch),
    [indexes]
  )

  return (
    <RepoSelectModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      repositories={repositories}
      getBranches={getBranches}
      title='Add Repository'
      initialSelectedRepos={initialRepos}
      initialUploadedRepos={initialUploads}
      workspaceUuid={workspaceUuid}
      requireSelection={false}
    />
  )
}

export function MonitoringDashboardDialog({ isOpen, onClose }: MonitoringDashboardDialogProps) {
  const navigate = useNavigate()
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceUuid = currentWorkspace?.uuid
  const isCompact = useCompactView()

  const [activeTab, setActiveTab] = useState<MonitoringTab>('recent-alerts')
  const [autoOpenManageScope, setAutoOpenManageScope] = useState(false)

  const [alerts, setAlerts] = useState<MonitoringAlert[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [nextOffset, setNextOffset] = useState(0)
  const [errorMessage, setErrorMessage] = useState('')

  const [filterMode, setFilterMode] = useState<MonitoringAlertFilterMode>('all_alerts')
  const [appliedStartTimeUtc, setAppliedStartTimeUtc] = useState<string>()
  const [appliedEndTimeUtc, setAppliedEndTimeUtc] = useState<string>()
  const [pendingStartLocal, setPendingStartLocal] = useState('')
  const [pendingEndLocal, setPendingEndLocal] = useState('')
  const [investigatingAlertId, setInvestigatingAlertId] = useState<number | null>(null)
  const [isInvestigateRepoModalOpen, setIsInvestigateRepoModalOpen] = useState(false)
  const [pendingInvestigateAlertId, setPendingInvestigateAlertId] = useState<number | null>(null)
  const [draftInvestigateRepositoryBranches, setDraftInvestigateRepositoryBranches] = useState<
    RepositoryBranch[]
  >([])
  const [draftInvestigateUploadedRepos, setDraftInvestigateUploadedRepos] = useState<
    UploadedRepo[]
  >([])

  const [integrations, setIntegrations] = useState<Integration[]>([])

  const refreshIntegrations = useCallback(async () => {
    if (!workspaceUuid) return
    try {
      const data = await fetchWrapper.get<Integration[]>(
        `/api/workspace/${workspaceUuid}/integration`,
        { credentials: 'include' }
      )
      setIntegrations(data)
    } catch {
      // silently ignore — connection tabs show their own empty state
    }
  }, [workspaceUuid])

  useEffect(() => {
    if (isOpen && workspaceUuid) {
      void refreshIntegrations()
    }
  }, [isOpen, workspaceUuid, refreshIntegrations])

  const alertsScrollRef = useRef<HTMLDivElement>(null)
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null)

  const hasAppliedDateTimeRange =
    filterMode === 'date_time_range' &&
    typeof appliedStartTimeUtc === 'string' &&
    typeof appliedEndTimeUtc === 'string'

  const loadMoreAlerts = useCallback(async () => {
    if (!isOpen || !workspaceUuid || isLoading || isFetchingMore || !hasMore) {
      return
    }

    setIsFetchingMore(true)

    try {
      const response = await getMonitoringAlerts(workspaceUuid, {
        limit: DEFAULT_ALERT_FETCH_LIMIT,
        offset: nextOffset,
        ...(hasAppliedDateTimeRange
          ? {
              startTime: appliedStartTimeUtc,
              endTime: appliedEndTimeUtc
            }
          : {})
      })

      setAlerts(previousAlerts => mergeMonitoringAlerts(previousAlerts, response))
      setNextOffset(previousOffset => previousOffset + response.length)
      setHasMore(response.length === DEFAULT_ALERT_FETCH_LIMIT)
    } catch (error) {
      const errorText =
        error instanceof Error ? error.message : 'Failed to load additional monitoring alerts.'
      setErrorMessage(errorText)
    } finally {
      setIsFetchingMore(false)
    }
  }, [
    appliedEndTimeUtc,
    appliedStartTimeUtc,
    hasAppliedDateTimeRange,
    hasMore,
    isFetchingMore,
    isLoading,
    isOpen,
    nextOffset,
    workspaceUuid
  ])

  useEffect(() => {
    if (isOpen) {
      return
    }

    setActiveTab('recent-alerts')
    setAutoOpenManageScope(false)
    setFilterMode('all_alerts')
    setAppliedStartTimeUtc(undefined)
    setAppliedEndTimeUtc(undefined)
    setPendingStartLocal('')
    setPendingEndLocal('')
    setAlerts([])
    setIntegrations([])
    setErrorMessage('')
    setHasMore(false)
    setNextOffset(0)
    setIsLoading(false)
    setIsFetchingMore(false)
    setInvestigatingAlertId(null)
    setIsInvestigateRepoModalOpen(false)
    setPendingInvestigateAlertId(null)
    setDraftInvestigateRepositoryBranches([])
    setDraftInvestigateUploadedRepos([])
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    if (!workspaceUuid) {
      setAlerts([])
      setHasMore(false)
      setNextOffset(0)
      setErrorMessage('Workspace not found')
      return
    }

    let disposed = false

    setIsLoading(true)
    setIsFetchingMore(false)
    setErrorMessage('')

    getMonitoringAlerts(workspaceUuid, {
      limit: DEFAULT_ALERT_FETCH_LIMIT,
      offset: 0,
      ...(hasAppliedDateTimeRange
        ? {
            startTime: appliedStartTimeUtc,
            endTime: appliedEndTimeUtc
          }
        : {})
    })
      .then(response => {
        if (disposed) {
          return
        }

        setAlerts(response)
        setNextOffset(response.length)
        setHasMore(response.length === DEFAULT_ALERT_FETCH_LIMIT)
      })
      .catch(error => {
        if (disposed) {
          return
        }

        const errorText =
          error instanceof Error ? error.message : 'Failed to load monitoring alerts.'
        setAlerts([])
        setNextOffset(0)
        setHasMore(false)
        setErrorMessage(errorText)
      })
      .finally(() => {
        if (!disposed) {
          setIsLoading(false)
        }
      })

    return () => {
      disposed = true
    }
  }, [appliedEndTimeUtc, appliedStartTimeUtc, hasAppliedDateTimeRange, isOpen, workspaceUuid])

  useEffect(() => {
    if (!isOpen || !hasMore || isLoading || typeof IntersectionObserver === 'undefined') {
      return
    }

    const root = alertsScrollRef.current
    const target = loadMoreSentinelRef.current
    if (!root || !target) {
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          void loadMoreAlerts()
        }
      },
      {
        root,
        rootMargin: '120px 0px 120px 0px',
        threshold: 0
      }
    )

    observer.observe(target)

    return () => {
      observer.disconnect()
    }
  }, [alerts.length, hasMore, isLoading, isOpen, loadMoreAlerts])

  const renderedAlerts = useMemo(
    () =>
      alerts.map(alert => ({
        ...alert,
        displayProvider: providerLabel(alert.provider),
        displayTitle: alertTitle(alert),
        displayTime: toAbsoluteDateTimeLabel(alert.created_time)
      })),
    [alerts]
  )

  const handleSelectAllAlerts = () => {
    setFilterMode('all_alerts')
    setAppliedStartTimeUtc(undefined)
    setAppliedEndTimeUtc(undefined)
    setPendingStartLocal('')
    setPendingEndLocal('')
  }

  const handleApplyDateTimeRange = (startLocal: string, endLocal: string) => {
    const startUtc = datetimeLocalToUtc(startLocal)
    const endUtc = datetimeLocalToUtc(endLocal)

    if (!startUtc || !endUtc) {
      setErrorMessage('Failed to apply date/time range.')
      return
    }

    setPendingStartLocal(startLocal)
    setPendingEndLocal(endLocal)
    setAppliedStartTimeUtc(startUtc)
    setAppliedEndTimeUtc(endUtc)
    setFilterMode('date_time_range')
  }

  const handleInvestigateContext = useCallback((alertId: number) => {
    setPendingInvestigateAlertId(alertId)
    setDraftInvestigateRepositoryBranches([])
    setDraftInvestigateUploadedRepos([])
    setIsInvestigateRepoModalOpen(true)
  }, [])

  const handleCloseInvestigateRepoModal = useCallback(() => {
    setIsInvestigateRepoModalOpen(false)
    setPendingInvestigateAlertId(null)
    setDraftInvestigateRepositoryBranches([])
    setDraftInvestigateUploadedRepos([])
  }, [])

  const handleStartInvestigationFromSelection = useCallback(
    async (
      alertId: number,
      repositoryBranches: RepositoryBranch[],
      uploadedRepos: UploadedRepo[]
    ) => {
      if (!workspaceUuid) {
        BetterToast.error('Workspace not found')
        return
      }
      if (investigatingAlertId !== null) {
        return
      }

      setInvestigatingAlertId(alertId)

      try {
        const s3UploadPaths = uploadedRepos
          .map(repo => repo.s3Path?.trim() ?? '')
          .filter(path => path.length > 0)

        const investigateRequest =
          repositoryBranches.length > 0 || s3UploadPaths.length > 0
            ? {
                ...(repositoryBranches.length > 0
                  ? { repository_branches: repositoryBranches }
                  : {}),
                ...(s3UploadPaths.length > 0 ? { s3_upload_paths: s3UploadPaths } : {})
              }
            : undefined
        const result = await investigateMonitoringAlert(workspaceUuid, alertId, investigateRequest)
        storePendingInitialMessage(result.conversation_uuid, result.initial_message)
        onClose()
        navigate(`/w/${workspaceUuid}/c/${result.conversation_uuid}`)
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to start investigation chat'
        BetterToast.error(message)
      } finally {
        setInvestigatingAlertId(null)
      }
    },
    [investigatingAlertId, navigate, onClose, workspaceUuid]
  )

  const handleConfirmInvestigateRepoSelection = useCallback(
    (data: { repositoryBranches: RepositoryBranch[]; uploadedRepos: UploadedRepo[] }) => {
      if (pendingInvestigateAlertId === null) {
        BetterToast.error('No alert selected for investigation')
        return
      }

      setDraftInvestigateRepositoryBranches(data.repositoryBranches)
      setDraftInvestigateUploadedRepos(data.uploadedRepos)
      void handleStartInvestigationFromSelection(
        pendingInvestigateAlertId,
        data.repositoryBranches,
        data.uploadedRepos
      )
    },
    [handleStartInvestigationFromSelection, pendingInvestigateAlertId]
  )

  const tabTitleMap: Record<MonitoringTab, string> = {
    'recent-alerts': 'Recent Alerts',
    'monitored-scope': 'Monitored Scope',
    connections: 'Connections',
    'shared-members': 'Shared Members'
  }
  const headerTitle = tabTitleMap[activeTab] ?? 'Monitoring'

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        hideCloseButton
        disableOutsideClick
        rawContent
        className={
          isCompact
            ? 'w-full max-w-[calc(100vw-24px)] h-[calc(100vh-32px)]'
            : 'w-full max-w-[1048px] h-[calc(100vh-120px)]'
        }
        overlayClassName={isCompact ? 'px-3' : 'px-[60px]'}
      >
        <div className={`flex flex-1 min-h-0 ${isCompact ? 'flex-col' : 'flex-row'}`}>
          {/* Sidebar — hidden in compact */}
          {!isCompact && (
            <aside className='bg-white border-r border-[#EAEAEA] flex w-[180px] min-w-[180px] py-[24px] px-[12px] flex-col items-start gap-[48px] self-stretch'>
              <div className='flex flex-col justify-center items-start w-full pl-[16px]'>
                <Typography
                  fontFamily='geist'
                  fontSize='titleSm'
                  fontWeight='bold'
                  className='text-black'
                >
                  Monitoring
                </Typography>
              </div>
              <div className='flex flex-col gap-[4px] w-full'>
                {sidebarItems.map(item => {
                  const Icon = item.icon
                  const isActive = activeTab === item.key
                  return (
                    <button
                      key={item.key}
                      type='button'
                      disabled={item.disabled}
                      onClick={() => {
                        setActiveTab(item.key)
                        setAutoOpenManageScope(false)
                      }}
                      className={[
                        'h-[36px] w-full rounded-[12px] px-[16px] flex items-center justify-between',
                        isActive ? 'bg-[rgba(0,0,0,0.06)]' : 'bg-transparent',
                        item.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      ].join(' ')}
                    >
                      <span className='flex min-w-0 items-center gap-[10px]'>
                        <Icon className='size-4 text-[rgba(0,0,0,0.88)]' />
                        <Typography
                          fontFamily='geist'
                          fontSize='bodyMdMedium'
                          fontWeight='medium'
                          className='whitespace-nowrap text-[rgba(0,0,0,0.88)]'
                        >
                          {item.label}
                        </Typography>
                      </span>
                    </button>
                  )
                })}
              </div>
            </aside>
          )}

          {/* Right side: header + content */}
          <div className='flex-1 min-w-0 flex flex-col min-h-0'>
            {/* Header */}
            <DialogHeader title={headerTitle} onClose={onClose} className='shrink-0 select-none'>
              <Typography
                fontFamily='geistMono'
                fontSize='labelSm'
                fontWeight='semibold'
                className='rounded-[6px] bg-tokens-primary-4 px-1.5 py-0.5 text-tokens-neutral-bg-white'
              >
                Beta
              </Typography>
            </DialogHeader>

            {/* Compact horizontal tab bar */}
            {isCompact && (
              <div
                className='px-3 py-2 border-b border-tokens-neutral-border-2 bg-white'
                data-testid='monitoring-compact-tabs'
              >
                <div className='flex items-center gap-2 overflow-x-auto no-scrollbar'>
                  {sidebarItems.map(item => {
                    const Icon = item.icon
                    return (
                      <Button
                        key={item.key}
                        variant='borderless'
                        onClick={() => {
                          setActiveTab(item.key)
                          setAutoOpenManageScope(false)
                        }}
                        isDisabled={item.disabled}
                        leftIcon={<Icon className='size-4' />}
                        className={`h-9 px-3 rounded-xl whitespace-nowrap font-medium text-[13px] leading-[20px] ${
                          activeTab === item.key
                            ? 'bg-tokens-neutral-fill-fill-6 text-tokens-neutral-text-primary'
                            : 'text-tokens-neutral-text-secondary'
                        }`}
                      >
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Content area */}
            <div className='flex-1 min-w-0 min-h-0 overflow-hidden bg-[#EFF0F3]'>
              {activeTab === 'recent-alerts' ? (
                <div
                  className={
                    isCompact
                      ? 'h-full overflow-hidden py-4 px-3'
                      : 'h-full overflow-hidden py-6 px-[48px]'
                  }
                >
                  <div className={`flex h-full min-h-0 flex-col ${isCompact ? 'gap-2' : 'gap-6'}`}>
                    <div
                      className={`flex shrink-0 ${isCompact ? 'items-center gap-2' : 'flex-col gap-6'}`}
                    >
                      {!isCompact && (
                        <Typography
                          fontFamily='geist'
                          fontSize='titleLg'
                          fontWeight='bold'
                          className='text-[rgba(0,0,0,0.88)]'
                        >
                          Recent Alerts
                        </Typography>
                      )}

                      <MonitoringAlertsFilterDropdown
                        filterMode={filterMode}
                        pendingStartLocal={pendingStartLocal}
                        pendingEndLocal={pendingEndLocal}
                        onSelectAllAlerts={handleSelectAllAlerts}
                        onApplyDateTimeRange={handleApplyDateTimeRange}
                        isCompact={isCompact}
                      />
                    </div>

                    <div
                      ref={alertsScrollRef}
                      className='min-h-0 flex-1 overflow-y-auto pr-[4px] pb-[4px]'
                      data-testid='monitoring-alerts-scroll'
                    >
                      {isLoading ? (
                        <Typography
                          fontFamily='geist'
                          fontSize='bodyMd'
                          className='text-[rgba(0,0,0,0.65)]'
                        >
                          Loading alerts...
                        </Typography>
                      ) : null}

                      {!isLoading && errorMessage ? (
                        <Typography
                          fontFamily='geist'
                          fontSize='bodyMd'
                          className='text-tokens-status-error-text'
                        >
                          {errorMessage}
                        </Typography>
                      ) : null}

                      {!isLoading && !errorMessage && renderedAlerts.length === 0 ? (
                        <Typography
                          fontFamily='geist'
                          fontSize='bodyMd'
                          className='text-[rgba(0,0,0,0.65)]'
                        >
                          No alerts in this workspace yet.
                        </Typography>
                      ) : null}

                      {!isLoading && !errorMessage && renderedAlerts.length > 0 ? (
                        <div className={`flex flex-col ${isCompact ? '' : 'gap-6'}`}>
                          {renderedAlerts.map(alert => (
                            <AlertItem
                              key={alert.id}
                              alert={alert}
                              isCompact={isCompact}
                              isInvestigating={alert.id === investigatingAlertId}
                              disabled={investigatingAlertId !== null}
                              onInvestigate={handleInvestigateContext}
                            />
                          ))}

                          <div
                            ref={loadMoreSentinelRef}
                            className='h-px w-full'
                            data-testid='monitoring-alerts-load-more-sentinel'
                          />

                          {isFetchingMore ? (
                            <Typography
                              fontFamily='geist'
                              fontSize='bodySm'
                              className='py-1 text-[rgba(0,0,0,0.45)]'
                            >
                              Loading more alerts...
                            </Typography>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              {activeTab === 'connections' && workspaceUuid ? (
                <MonitoringConnectionsContent
                  workspaceUuid={workspaceUuid}
                  integrations={integrations}
                  refreshIntegrations={refreshIntegrations}
                  isCompact={isCompact}
                  onConnectionSetup={() => {
                    setAutoOpenManageScope(true)
                    setActiveTab('monitored-scope')
                  }}
                />
              ) : null}

              {activeTab === 'monitored-scope' && workspaceUuid ? (
                <MonitoringMonitoredScopeContent
                  workspaceUuid={workspaceUuid}
                  integrations={integrations}
                  refreshIntegrations={refreshIntegrations}
                  isCompact={isCompact}
                  autoOpenManageScope={autoOpenManageScope}
                />
              ) : null}
            </div>
          </div>
        </div>
      </Modal>

      {workspaceUuid && isInvestigateRepoModalOpen ? (
        <MonitoringInvestigateRepoModal
          isOpen={isInvestigateRepoModalOpen}
          workspaceUuid={workspaceUuid}
          initialRepos={draftInvestigateRepositoryBranches}
          initialUploads={draftInvestigateUploadedRepos}
          onClose={handleCloseInvestigateRepoModal}
          onConfirm={handleConfirmInvestigateRepoSelection}
        />
      ) : null}
    </>
  )
}

export default MonitoringDashboardDialog
